import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import type { TrainingProgramActivation } from '../../types/api'
import type { WorkoutSession } from '../../api/workoutApi'
import ActiveWorkoutWidget from './ActiveWorkoutWidget'

export type SoloLibraryState = {
  blockCount: number
  programCount: number
}

interface SoloTrainingWidgetProps {
  activeProgram: TrainingProgramActivation | null
  activeWorkout: WorkoutSession | null
  library?: SoloLibraryState | null
}

function currentProgramWeek(startedAt: string): number {
  const elapsed = Math.floor(
    (Date.now() - new Date(startedAt).getTime()) / (7 * 24 * 60 * 60 * 1000)
  )
  return elapsed + 1
}

/**
 * Solo trainee home hero — empty library / activate program / active program / resume.
 */
export default function SoloTrainingWidget({
  activeProgram,
  activeWorkout,
  library,
}: SoloTrainingWidgetProps) {
  const navigate = useNavigate()
  const { t, translate } = useTranslation()
  const { user } = useAuth()
  const canUseWorkoutMode = entitlementsOf(user).workoutMode
  const copy = t.traineeDashboard.emptyState.getStarted
  const programCopy = t.traineeDashboard.activeProgram

  const blockCount = library?.blockCount ?? 0
  const programCount = library?.programCount ?? 0

  if (activeWorkout?.id != null) {
    return <ActiveWorkoutWidget workout={activeWorkout} />
  }

  if (activeProgram) {
    const week = Math.min(
      currentProgramWeek(activeProgram.startedAt),
      activeProgram.durationWeeks
    )
    const progress = week / activeProgram.durationWeeks

    return (
      <HeroShell
        tourId="tour-trainee-get-started"
        onClick={() => navigate(`/training-programs/${activeProgram.programId}`)}
        badge={programCopy.title}
        chip={translate('traineeDashboard.activeProgram.weekOf', {
          current: week,
          total: activeProgram.durationWeeks,
        })}
        title={activeProgram.programName}
        subtitle={null}
        progressLabel={copy.activeProgress}
        progressPct={Math.round(progress * 100)}
        ctaLabel={canUseWorkoutMode ? t.workout.startTitle : t.workout.viewProgram}
        ctaColor="#005f8f"
        onCta={() => navigate(`/training-programs/${activeProgram.programId}`)}
      />
    )
  }

  // No active program — tailor empty state to library readiness
  const emptyVariant =
    blockCount === 0
      ? 'noBlocks'
      : programCount === 0
        ? 'noPrograms'
        : 'activate'

  const emptyCopy =
    emptyVariant === 'noBlocks'
      ? {
          chip: copy.notStarted,
          title: copy.noBlocksTitle,
          subtitle: copy.noBlocksSubtitle,
          action: copy.noBlocksAction,
          path: '/training-blocks',
        }
      : emptyVariant === 'noPrograms'
        ? {
            chip: copy.notStarted,
            title: copy.noProgramsTitle,
            subtitle: copy.noProgramsSubtitle,
            action: copy.noProgramsAction,
            path: '/training-programs',
          }
        : {
            chip: copy.notStarted,
            title: copy.title,
            subtitle: copy.subtitle,
            action: copy.action,
            path: '/training-programs',
          }

  return (
    <HeroShell
      tourId="tour-trainee-get-started"
      onClick={() => navigate(emptyCopy.path)}
      badge={copy.badge}
      chip={emptyCopy.chip}
      title={emptyCopy.title}
      subtitle={emptyCopy.subtitle}
      progressLabel={copy.activeProgress}
      progressPct={0}
      ctaLabel={emptyCopy.action}
      ctaColor="#005f8f"
      onCta={() => navigate(emptyCopy.path)}
    />
  )
}

function HeroShell({
  tourId,
  onClick,
  badge,
  chip,
  title,
  subtitle,
  progressLabel,
  progressPct,
  ctaLabel,
  ctaColor,
  onCta,
}: {
  tourId: string
  onClick: () => void
  badge: string
  chip: string
  title: string
  subtitle: string | null
  progressLabel: string
  progressPct: number
  ctaLabel: string
  ctaColor: string
  onCta: () => void
}) {
  return (
    <div
      data-tour={tourId}
      className="mb-5 rounded-2xl p-5 cursor-pointer active:scale-[0.98] transition-transform relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #005f8f 0%, #0088cc 55%, #3ba8dc 100%)',
        boxShadow: '0 8px 32px rgba(0, 136, 204, 0.35)',
      }}
      onClick={onClick}
    >
      <Decor />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <span
            className="text-xs font-semibold tracking-widest uppercase"
            style={{ color: 'rgba(255,255,255,0.65)' }}
          >
            {badge}
          </span>
          <span
            className="rounded-lg px-2.5 py-1 text-xs font-bold"
            style={{ background: 'rgba(255,255,255,0.18)', color: 'white' }}
          >
            {chip}
          </span>
        </div>

        <div className="text-lg font-bold mb-1" style={{ color: 'white' }}>
          {title}
        </div>
        {subtitle && (
          <p className="text-xs leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.78)' }}>
            {subtitle}
          </p>
        )}
        {!subtitle && <div className="mb-4" />}

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
              {progressLabel}
            </span>
            <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.9)' }}>
              {progressPct}%
            </span>
          </div>
          <div
            className="rounded-full overflow-hidden h-2"
            style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progressPct}%`,
                background: 'rgba(255,255,255,0.95)',
              }}
            />
          </div>
        </div>

        <button
          type="button"
          className="w-full rounded-xl py-2.5 text-sm font-semibold active:scale-[0.98] transition-transform"
          style={{ background: 'rgba(255,255,255,0.95)', color: ctaColor }}
          onClick={(e) => {
            e.stopPropagation()
            onCta()
          }}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  )
}

function Decor() {
  return (
    <>
      <div
        className="absolute pointer-events-none"
        style={{
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          top: -40,
          right: -40,
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          bottom: -20,
          right: 60,
        }}
      />
    </>
  )
}
